import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { cn, formatDateValue } from "@/lib/utils";
import { useDiabolo } from "@/providers/diabolocom";
import { ArrowUp, Brain, ExternalLink, PhoneCall, Loader2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { SimpleTooltip } from "../ui/simple-tooltip";
import { SmsCredits, type ConversationDetail, type Task, type SmsCorrection } from "@/types/crm/sms";
import { DataService } from "@/services/data";
import { toast } from "sonner";
import { useUser } from "@/providers/user";

interface ConversationPanelProps {
    conversation: ConversationDetail | null;
    messages: Task[];
    smsCredits: SmsCredits | null;
    isLoading?: boolean;
    onCall?: (phone: string) => void;
}

export function ConversationPanel({ conversation, smsCredits, messages, isLoading = false, onCall }: ConversationPanelProps) {
    const { user } = useUser();
    const [input, setInput] = useState("");
    const [isAICorrecting, setIsAICorrecting] = useState(false);
    const [aiCorrection, setAiCorrection] = useState<SmsCorrection | null>(null);
    const [isSending, setIsSending] = useState(false); // Add this state
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const aiAbortControllerRef = useRef<AbortController | null>(null);
    const { dialNumber } = useDiabolo();
    const orderedMessages = useMemo(() => {
        return [...messages].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    }, [messages]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "instant" });
    }, [orderedMessages.length, conversation?.mobile_phone, bottomRef.current]);

    const autoResize = () => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
    };

    useEffect(() => {
        autoResize();
    }, [input, textareaRef.current, bottomRef.current]);

    useEffect(() => {
        aiAbortControllerRef.current?.abort();
        aiAbortControllerRef.current = null;
        setInput("");
        setAiCorrection(null);
        setIsAICorrecting(false);

        // Focus the textarea when conversation changes
        if (conversation?.id) {
            setTimeout(() => {
                textareaRef.current?.focus();
            }, 100);
        }
    }, [conversation?.id]);

    // Group by day for separators (without Map)
    const groups = useMemo(() => {
        const result: Array<[string, Task[]]> = [];
        let lastKey: string | null = null;
        for (const m of orderedMessages) {
            const key = new Date(m.created_at).toDateString();
            if (key !== lastKey) {
                result.push([key, [m]]);
                lastKey = key;
            } else {
                result[result.length - 1][1].push(m);
            }
        }
        return result;
    }, [orderedMessages]);

    // Determine display header (use already ordered messages)
    const headerInfo = useMemo(() => {
        const account = conversation?.sf_contact || conversation?.sf_account || conversation?.sf_lead || null;
        return {
            accountName: account?.name || conversation?.mobile_phone || "",
            accountId: account?.id ?? null,
            accountPhone: conversation?.mobile_phone ?? null,
            linkHref: conversation?.sf_contact?.href || conversation?.sf_account?.href || conversation?.sf_lead?.href || null
        };
    }, [conversation]);

    const renderMessageWithLinks = (text: string) => {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const parts: Array<string | JSX.Element> = [];
        let lastIndex = 0;
        let match: RegExpExecArray | null;
        while ((match = urlRegex.exec(text)) !== null) {
            const [url] = match;
            const start = match.index;
            const end = start + url.length;
            if (start > lastIndex) {
                parts.push(text.slice(lastIndex, start));
            }
            parts.push(
                <a key={`${start}-${end}`} href={url} target="_blank" rel="noopener noreferrer" className="whitespace-pre-wrap break-words break-all underline">
                    {url}
                </a>
            );
            lastIndex = end;
        }
        if (lastIndex < text.length) {
            parts.push(text.slice(lastIndex));
        }
        return parts;
    };

    const hasCredits = Boolean(smsCredits && smsCredits.remaining_sms > 0);
    const hasPhone = Boolean(conversation?.mobile_phone);
    const isInputEmpty = input.trim().length === 0;
    const isTextareaDisabled = !hasCredits || isAICorrecting || isLoading || isSending; // Update this
    const aiDisabled = isLoading || isAICorrecting || !hasCredits || !hasPhone || isInputEmpty || isSending; // Update this
    const sendDisabled = aiDisabled || aiCorrection == null || isSending; // Update this
    const inputPlaceholder = hasCredits ? "Write a message..." : "You have no credits left";

    const handleSendMessage = async () => {
        if (sendDisabled || isSending) return; // Add this check

        setIsSending(true); // Add this
        try {
            const controller = new AbortController();
            const response = await DataService.client
                .signal(controller)
                .url(`/crm/app/conversations/${conversation?.id}/send_sms/`)
                .post({
                    message: input
                })
                .json<any>();

            // Check if the response contains an error
            if (response && response.error) {
                console.error("Send SMS failed:", response.error);
                toast.error(`Failed to send SMS: ${response.error}`);
                return;
            }

            setInput("");
            setAiCorrection(null);
            toast.success("SMS sent successfully");
        } catch (error: any) {
            if (error?.name !== "AbortError") {
                console.error("Send SMS failed:", error);
                toast.error("Failed to send SMS. Please try again later.");
            }
        } finally {
            setIsSending(false); // Add this
        }
    };

    const handleAICorrection = async () => {
        setIsAICorrecting(true);
        setAiCorrection(null);

        try {
            // Abort previous AI request if still pending
            aiAbortControllerRef.current?.abort();
            const controller = new AbortController();
            aiAbortControllerRef.current = controller;
            const result = await DataService.client
                .signal(controller)
                .url(`/crm/app/conversations/${conversation?.id}/correct_sms/`)
                .post({
                    message: input
                })
                .json<SmsCorrection>();

            setAiCorrection(result);
            if (result.success) setInput(result.corrected_message);
        } catch (error: any) {
            if (error?.name !== "AbortError") {
                console.error("AI correction failed:", error);
                setAiCorrection({
                    success: false,
                    original_message: input,
                    corrected_message: "",
                    error: "Failed to correct message"
                });
            }
        } finally {
            setIsAICorrecting(false);
            aiAbortControllerRef.current = null;
        }
    };

    if (isLoading) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-gray-600" />
            </div>
        );
    }

    return (
        <div className="relative flex h-full min-w-0 flex-1 flex-col overflow-hidden rounded-r-md bg-white">
            <div className="absolute inset-x-0 top-0 z-10  bg-white/60 px-4 py-2 backdrop-blur-2xl supports-[backdrop-filter]:bg-white/60">
                <div className="flex w-full min-w-0 items-center justify-between gap-1">
                    <div className="flex items-center gap-2 truncate text-sm font-semibold">
                        {headerInfo.accountName && (
                            <SimpleTooltip message={headerInfo.accountPhone ?? ""} delayDuration={0} side="bottom" align="center">
                                <span title={headerInfo.accountPhone ?? undefined}>{headerInfo.accountName}</span>
                            </SimpleTooltip>
                        )}
                        {headerInfo.accountId && headerInfo.accountPhone && (
                            <div
                                onClick={() => {
                                    if (headerInfo.accountPhone) {
                                        onCall?.(headerInfo.accountPhone);
                                        dialNumber(headerInfo.accountPhone);
                                    }
                                }}
                                className={cn("mb-0.5 flex aspect-square h-7 w-7 cursor-pointer items-center justify-center self-end rounded-full  bg-gray-100 hover:bg-gray-200")}
                            >
                                <PhoneCall className="size-4" />
                            </div>
                        )}
                    </div>
                    {headerInfo.linkHref && (
                        <NavLink to={headerInfo.linkHref} className="text-sm text-blue-600 underline-offset-4 hover:underline">
                            {conversation?.sf_contact ? "View contact details" : conversation?.sf_account ? "View account details" : "View lead details"}
                            <ExternalLink className="ml-2 inline-block -translate-y-0.5" size={14} />
                        </NavLink>
                    )}
                </div>
            </div>

            <ScrollArea className="flex-1">
                <div className="flex flex-col gap-3 p-3 pb-24 pt-20">
                    {isLoading ? (
                        <div className="flex h-40 items-center justify-center">
                            <Loader2 className="h-6 w-6 animate-spin text-gray-600" />
                        </div>
                    ) : (
                        <>
                            {groups.map(([day, dayMessages]) => (
                                <div key={day} className="flex flex-col gap-3">
                                    <div className="relative my-3 flex items-center justify-center">
                                        <span className="absolute rounded-full bg-white px-2 text-[10px] font-medium text-gray-400">{formatDateValue(new Date(day), "long")}</span>
                                    </div>
                                    {dayMessages.map(msg => {
                                        const isOutbound = !msg.is_inbound;
                                        return (
                                            <div key={msg.id} className={cn("flex w-full", isOutbound ? "justify-end" : "justify-start")}>
                                                <div className={cn("group flex max-w-[60%] flex-col items-start", isOutbound ? "items-end" : "items-start")}>
                                                    <div className={cn("rounded-xl px-3 py-2 text-sm shadow-sm", isOutbound ? "bg-azure-blue text-white" : "bg-gray-100 text-gray-900")}>
                                                        <div className="whitespace-pre-wrap break-words">{renderMessageWithLinks(msg.description || "")}</div>
                                                    </div>
                                                    <div className={cn("mt-0.5 flex items-center gap-1", isOutbound ? "flex-row-reverse" : "flex-row")}>
                                                        {isOutbound && (msg.created_by?.first_name || msg.created_by?.last_name) && (
                                                            <div className="flex items-center gap-1">
                                                                <span className="text-[10px] text-gray-500">
                                                                    by {`${(msg.created_by?.first_name || "").trim()} ${(msg.created_by?.last_name || "").trim()}`.trim()}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div className="text-[10px] text-gray-500 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                                                            at {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </>
                    )}
                    <div ref={bottomRef} />
                </div>
            </ScrollArea>

            <div className="absolute inset-x-0 bottom-0 z-10 bg-white/60 p-2 backdrop-blur-2xl supports-[backdrop-filter]:bg-white/60">
                <form
                    className="flex items-center gap-2"
                    onSubmit={e => {
                        e.preventDefault();
                        handleSendMessage();
                    }}
                >
                    <div className="flex w-full items-end gap-1 rounded-[20px] border py-0.5 pl-4 pr-[5px]">
                        <Textarea
                            ref={textareaRef}
                            className="scrollbar-hide max-h-40 min-h-[2rem] resize-none border-0 p-0 py-1.5 shadow-none focus-visible:ring-0"
                            disabled={isTextareaDisabled}
                            maxLength={160}
                            placeholder={inputPlaceholder}
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={e => {
                                // Laisser Enter créer une nouvelle ligne (par défaut Textarea gère \n)
                                // Empêcher Ctrl/Cmd+Enter d'envoyer tant qu'on n'a pas branché de backend
                                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                                    e.preventDefault();
                                }
                            }}
                            rows={1}
                        />
                        <div
                            onClick={handleAICorrection}
                            className={cn(
                                "mb-0.5 flex aspect-square h-7 w-7 cursor-pointer items-center justify-center self-end rounded-full",
                                aiDisabled ? "pointer-events-none bg-gray-100 opacity-50" : "bg-azure-blue text-white"
                            )}
                        >
                            {isAICorrecting ? <Loader2 className="text-grey size-4 animate-spin" /> : <Brain className="size-4" />}
                        </div>
                        <div
                            onClick={handleSendMessage}
                            className={cn(
                                "mb-0.5 flex aspect-square h-7 w-7 cursor-pointer items-center justify-center self-end rounded-full",
                                sendDisabled ? "pointer-events-none bg-gray-100 opacity-50" : "bg-azure-blue text-white"
                            )}
                        >
                            {isSending ? <Loader2 className="size-4 animate-spin" /> : <ArrowUp className="size-4" />}
                        </div>
                    </div>
                </form>
                <div className="flex items-center justify-between gap-2 px-2 pt-0.5">
                    <div className={cn("flex items-center gap-2", isTextareaDisabled ? "pointer-events-none bg-gray-100 opacity-50" : "")}>
                        <span className={cn("text-[10px] text-gray-500", input.length > 160 ? "text-red-700" : "")}>{input.length} / 160 chars</span>
                        {aiCorrection && <span className={cn("text-[10px]", aiCorrection.success ? "text-green-600" : "text-red-600")}>{aiCorrection.success ? "✓ AI corrected" : "✗ AI failed"}</span>}
                    </div>
                </div>
                {user?.sf_user?.phone_number == null && (
                    <div className="px-2">
                        <span className="text-[10px] text-amber-600">⚠ The receiver has only 20 minutes to respond to this SMS.</span>
                    </div>
                )}
            </div>
        </div>
    );
}
